import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  EventWithRelationsDto,
  UpdateEventDto,
  ResponseDto,
} from "@mimber/schema"
import { eventsKeys } from "../events.utils"

interface UpdateEventOptions extends Omit<
  UseMutationOptions<ResponseDto<EventWithRelationsDto>, Error, UpdateEventDto>,
  "mutationFn"
> {}

export const useUpdateEvent = (
  eventUuid: string,
  options: UpdateEventOptions = {}
): UseMutationResult<
  ResponseDto<EventWithRelationsDto>,
  Error,
  UpdateEventDto
> => {
  guardAppUsage("mimber-organization", "useUpdateEvent")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: UpdateEventDto) => {
      const response = await server.patch<ResponseDto<EventWithRelationsDto>>(
        `events/${eventUuid}`,
        data
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: eventsKeys.findAll(),
      })
      queryClient.invalidateQueries({
        queryKey: eventsKeys.findOne(eventUuid),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
